import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import styles from "./FlyingModal.module.css";
import { displayContentModal } from "../../../utils";
import { useGSAP } from "@gsap/react";
import ImageSlider from "../ImageSlider/ImageSlider";
import { gsap } from "gsap";
import { setCurrentModule } from "../../../store/reducers/progressionReducer";
import { useDispatch } from "react-redux";
import StandardPanel from "../../SvgComponents/StandardPanel";
import ButtonClose from "../../Componenents/ButtonClose/ButtonClose";
import InputNavigation from "../../Componenents/InputNavigation/InputNavigation";
import { div } from "three/examples/jsm/nodes/math/OperatorNode";
import ImageSolo from "../ImageSlider/ImageSolo";

/**
 * Displays content in a modal with optional pagination.
 * The modal can show text, images, or buttons based on the provided priorities.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the modal.
 * @param {Array} props.priorities - An array defining the order of content.
 * @param {Array} props.texts - An array of text strings to display in the modal.
 * @param {Array} props.images - An array of image objects to display in the modal.
 * @param {Array} props.buttons - An array of button objects with href and content.
 * @param {boolean} props.pagination - A boolean to enable/disable pagination.
 *
 * @return {JSX.Element} A JSX element representing the modal.
 */
export default function FlyingModal({
  title,
  priorities,
  texts,
  images,
  buttons,
  pagination,
  introContent,
  listRef,
  modalStyle,
}) {
  const [idx, setIdx] = useState(0);
  const { contextSafe } = useGSAP();
  const dispatch = useDispatch();
  const pageHolderRef = useRef(null);

  // Fix pour Safari iOS - Ajouter une classe pour détecter Safari iOS
  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isSafari && isIOS) {
      document.body.classList.add('ios-safari');
    }

    return () => {
      document.body.classList.remove('ios-safari');
    };
  }, []);

  useGSAP(() => {
    gsap.to(".modalFrame", { opacity: 1, duration: 1 });
  });

  useGSAP(
    () => {
      //Animation page
    },
    { dependencies: [idx] },
  );

  /**
   * Displays content in a modal with pagination.
   * This function iterates through a list of priorities, determining whether
   * to display text, images, or page content based on the elements in the list.
   *
   * @return {Array} An array of JSX elements representing the paginated modal content.
   */
  function displayContentModalPagination() {
    let indexText = 0;
    let indexImage = 0;
    let indexButton = 0;
    let indexIntroduction = 0;

    return priorities.map((elt, i) => {
      if (Array.isArray(elt)) {
        return displayPageContent(
          elt,
          i,
          indexText,
          indexImage,
          indexButton,
          indexIntroduction,
        );
      } else if (elt === "text") {
        return (
          <div
            key={uuidv4()}
            className={`${styles.page} ${i === idx && styles.showed}`}
            style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }}
          >
            <p className={styles.text} key={uuidv4()}>
              {texts[++indexText]}
            </p>
          </div>
        );
      } else {
        const currentImage = images[indexImage++];
        return (
          <div
            key={uuidv4()}
            className={`${styles.page} ${i === idx && styles.showed}`}
            style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }}
          >
            {currentImage.type && currentImage.type === "slider" ? (
              <ImageSlider key={uuidv4()} images={currentImage} adaptiveHeight={true}></ImageSlider>
            ) : (
              <ImageSolo key={uuidv4()} images={currentImage} adaptiveHeight={true}></ImageSolo>
            )}
          </div>
        );
      }
    });
  }

  /**
   * Displays the content of a page based on the given priorities and indices.
   *
   * @function
   * @param {Array} priorities - An array defining the order of content types to be rendered. Each element should be a string that specifies the type of content ("text", "image", or "button").
   * @param {number} i - The current index used to apply specific conditional styles or classes. Typically used for comparison with another index like `idx` for conditional rendering.
   * @param {number} indexText - The initial index for the text content array. This index is incremented as text elements are rendered.
   * @param {number} indexImage - The initial index for the image content array. This index is incremented as image elements are rendered.
   * @param {number} indexButton - The initial index for the button content array. This index is incremented as button elements are rendered.
   * @return {JSX.Element} The constructed JSX element representing the page content.
   */
  function displayPageContent(
    priorities,
    i,
    indexText,
    indexImage,
    indexButton,
    indexIntroduction,
  ) {
    return (
      <div
        key={uuidv4()}
        className={`${styles.page} ${i === idx && styles.showed}`}
        style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }}
      >
        {priorities.map((elt, subIdx) => {
          if (elt === "text") {
            return (
              <div style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }} key={uuidv4()}>
                <p className={styles.text} key={uuidv4()}>
                  {texts[indexText++]}
                </p>
              </div>
            );
          } else if (elt === "image") {
            const currentImage = images[indexImage++];
            return (
              <div style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }} key={uuidv4()}>
                {currentImage.type && currentImage.type === "slider" ? (
                  <ImageSlider key={uuidv4()} images={currentImage} adaptiveHeight={true}></ImageSlider>
                ) : (
                  <ImageSolo key={uuidv4()} images={currentImage} adaptiveHeight={true}></ImageSolo>
                )}
              </div>
            );
          } else if (elt === "button") {
            return (
              <a
                href={buttons[indexButton].href}
                key={uuidv4()}
                className={styles.link}
                target="_blank"
                style={{ maxWidth: '100%', overflow: 'hidden' }}
              >
                <span>{buttons[indexButton++].content}</span>
                <svg
                  width="11"
                  height="8"
                  viewBox="0 0 11 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.3536 4.35355C10.5488 4.15829 10.5488 3.84171 10.3536 3.64645L7.17157 0.464466C6.97631 0.269204 6.65973 0.269204 6.46447 0.464466C6.2692 0.659728 6.2692 0.976311 6.46447 1.17157L9.29289 4L6.46447 6.82843C6.2692 7.02369 6.2692 7.34027 6.46447 7.53553C6.65973 7.7308 6.97631 7.7308 7.17157 7.53553L10.3536 4.35355ZM0 4.5L10 4.5V3.5L0 3.5L0 4.5Z"
                    fill="#cf2e2e"
                  />
                </svg>
              </a>
            );
          } else if (elt === "intro") {
            return (
              <div className={styles.intro} style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                <h3 className={styles.title}>{title}</h3>
                <div className={styles.introContent} style={{ maxWidth: '100%', overflow: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                  <h4>{introContent[indexIntroduction][0]}</h4>
                  <p>{introContent[indexIntroduction++][1]}</p>
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  }

  /**
   * Renders a series of radio buttons allowing navigation between different priority pages.
   *
   * The function displays next and previous buttons to navigate through different pages.
   * It also uses radio inputs to allow selecting a specific page directly.
   *
   * @return {JSX.Element} The JSX code to render the navigation buttons and radio inputs.
   */
  function displayInputsRadio() {
    const lengthPages = priorities.length - 1;
    return (
      <div className={styles.navigationPages}>
        {priorities.map((elt, i) => (
          <div className={styles.radio} key={uuidv4()}>
            <InputNavigation i={i} idx={idx} setIdx={setIdx}></InputNavigation>
          </div>
        ))}
      </div>
    );
  }

  const closeEvent = contextSafe(() => {
    gsap
      .timeline({
        onComplete: () => {
          dispatch(setCurrentModule("none"));
        },
      })
      .to(".modalFrame", {
        opacity: 0,
        duration: 1,
        ease: "power2.out",
        onComplete: () => {
          if (listRef && listRef.current) {
            listRef.current.parentElement.classList.remove("wrapperModal");
          }
        },
      });
  });

  // Wrapper pour le contenu avec gestion de débordement
  const ContentWrapper = ({ children }) => {
    return (
      <div style={{
        maxWidth: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch',
        transform: 'translateZ(0)', // Force hardware acceleration
        willChange: 'transform' // Optimisation des performances
      }}>
        {children}
      </div>
    );
  };

  return (
    <div className={`${styles.FlyingModal} ${modalStyle}`}>
      <button className={styles.close} onClick={closeEvent}></button>
      <div className={styles.pages}>
        <div
          className={styles.pageHolder}
          ref={pageHolderRef}
          style={{
            WebkitOverflowScrolling: 'touch',
            transform: 'translateZ(0)',
            willChange: 'transform'
          }}
        >
          <ContentWrapper>
            {pagination === true ? (
              <>
                {displayContentModalPagination()}
                {displayInputsRadio()}{" "}
              </>
            ) : pagination === false ? (
              displayContentModalPagination()
            ) : (
              displayContentModal(
                priorities,
                texts,
                images,
                styles,
                buttons,
                introContent,
                title,
              )
            )}
          </ContentWrapper>
        </div>
      </div>
    </div>
  );
}
